import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from 'environments/environment';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, ReplaySubject, Subject, timer } from 'rxjs';
import { debounce, debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { UserCultureEnum } from '../enums/user-culture.enum.model';
import { IPagedRequest } from '../interfaces/paged-request.interface';
import { ActiveChannelDTO, ChannelDTO } from '../models/channel.model';
import { PagedResponse } from '../models/paged-response.model';
import { PointOfSaleDto } from '../models/point-of-sale.model';
import { Shop } from '../models/shop.model';
import { UserInfo } from '../models/user-info.model';
import { LoaderService } from './loader.service';
import { OrderTypeEnum } from '../enums/order-type.enum.model';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ActiveCartsCount, Order } from 'app/shop/shared/models/order.model';
import { StorageContextModel } from '../models/storage-context.model';
import { RolesEnum } from '../enums/roles.enum';
import { CurrentOS } from '../enums/current-os.enum';
import { Showcase } from '../models/showcases.model';
import { UtilityFunctions } from '../utilities/functions.models';

export type NavigationMode = 'assortment' | 'promotion';

@Injectable()
export class CoreService {
  api = environment.urls.apiUrl.url;

  private currentShop = new BehaviorSubject<Shop>(null);
  public currentShopChanged$ = this.currentShop.asObservable().pipe(distinctUntilChanged((prev, curr) => UtilityFunctions.isEquals(prev, curr)), shareReplay(1));
  public get currentShopValue() { return this.currentShop.value; };

  private currentActivatedChannel = new BehaviorSubject<ActiveChannelDTO>(null);
  public currentActivatedChannelChanged$ = this.currentActivatedChannel.asObservable().pipe(distinctUntilChanged((prev, curr) => UtilityFunctions.isEquals(prev, curr)), shareReplay(1));
  public get currentActivatedChannelValue() { return this.currentActivatedChannel.value; };

  private currentPos = new BehaviorSubject<PointOfSaleDto>(null);
  public currentPosChanged$ = this.currentPos.asObservable();
  public get currentPosValue() { return this.currentPos.value; };

  private _channels = new BehaviorSubject<ActiveChannelDTO[]>(null);
  public get channelsValue(): ActiveChannelDTO[] { return this._channels.value; };
  public get channelsSubject(): Observable<ActiveChannelDTO[]> { return this._channels.asObservable(); };

  private _contextSub = new BehaviorSubject<{ UserInfo: UserInfo, Stores: Shop[]; }>(null);
  public get contextValue(): { UserInfo: UserInfo, Stores: Shop[]; } { return this._contextSub.value; };
  public get contextSubject(): Observable<{ UserInfo: UserInfo, Stores: Shop[]; }> { return this._contextSub.asObservable(); };

  private userInfo = new BehaviorSubject<UserInfo>(null);
  public userInfoChanged$ = this.userInfo.asObservable();
  public get userInfoChangedValue() { return this.userInfo.value; }

  private shopLoading = new BehaviorSubject<boolean>(false);
  public toggleShopLoader$ = this.shopLoading.asObservable();

  private currentChannelName = new BehaviorSubject<string>('');
  public currentChannelName$ = this.currentChannelName.asObservable();
  public get currentChannelNameValue() { return this.currentChannelName.value; }

  private selectedReorderCart = new BehaviorSubject<Order>(null);
  public get selectedReorderCartChanged() { return this.selectedReorderCart.asObservable(); }
  public setSelectedReorderCart(cart: Order) { this.selectedReorderCart.next(cart); }

  private selectedPreorderCart = new BehaviorSubject<Order>(null);
  public get selectedPreorderCartChanged() { return this.selectedPreorderCart.asObservable(); }
  public setSelectedPreorderCart(cart: Order) { this.selectedPreorderCart.next(cart); }

  private activeCartsCount = new BehaviorSubject<ActiveCartsCount>(null);
  public setCurrentlyActiveCartsCount(count: ActiveCartsCount) { this.activeCartsCount.next(count); }
  public get currentlyActiveCartsCount() { return this.activeCartsCount.asObservable(); }

  private _currentShowcase = new BehaviorSubject<Showcase>(null);
  public currentShowcase$ = this._currentShowcase.asObservable().pipe(distinctUntilChanged((prev, curr) => UtilityFunctions.isEquals(prev, curr)), shareReplay(1));
  public get currentShowcase() { return this._currentShowcase.value; }
  public set currentShowcase(showcase: Showcase) { this._currentShowcase.next(showcase); }

  private _showcases = new BehaviorSubject<Showcase[]>(null);
  public showcases$ = this._showcases.asObservable();
  public get showcases() { return this._showcases.value; }
  public set showcases(showcases: Showcase[]) { this._showcases.next(showcases); }

  private currentSelectedCart = new BehaviorSubject<Order>(undefined);
  public currentSelectedCart$ = this.currentSelectedCart.asObservable();
  public get currentSelectedCartValue() { return this.currentSelectedCart.value; }
  public set currentSelectedCartValue(cart: Order) { this.currentSelectedCart.next(cart); }

  private currentAddingInfo = new BehaviorSubject<{ totalPcs: number, totalWholesale: number, loading: boolean }>(undefined);
  public currentAddingInfo$ = this.currentAddingInfo.asObservable();
  public get currentAddingInfoValue() { return this.currentAddingInfo.value; }
  public set currentAddingInfoValue(data: { totalPcs: number, totalWholesale: number, loading: boolean }) { this.currentAddingInfo.next(data); }

  private currentShopMode = new BehaviorSubject<OrderTypeEnum>(null);
  public currentShopMode$ = this.currentShopMode.asObservable().pipe(
    filter(mode => mode !== null && mode !== undefined),
    distinctUntilChanged((prev, curr) => prev === curr),
    shareReplay(1)
  );
  public get currentShopModeValue() { return this.currentShopMode.value; }

  private navigationOnlyRetailMode = new BehaviorSubject<boolean>(null);
  public currentNavigationOnlyRetailMode$ = this.navigationOnlyRetailMode.asObservable();
  public get currentNavigationOnlyRetailModeValue() { return this.navigationOnlyRetailMode.value; }
  public set currentNavigationOnlyRetailMode(data: { value: boolean, blockUpdateLocalStorage?: boolean }) {
    this.navigationOnlyRetailMode.next(data.value);
    if (!data.blockUpdateLocalStorage) {
      this.updateLocalStorageIfChanged('onlyRetailMode', data.value);
    }
  }

  public isUserGuest = new BehaviorSubject<boolean>(false);
  public isUserGuest$ = this.isUserGuest.asObservable();
  public get isUserGuestValue() { return this.isUserGuest.value; }

  public get currentOS(): CurrentOS { return this.detectCurrentOS().os; }
  public get isMobile(): boolean { return this.detectCurrentOS().isMobile; }

  private _navigationMode = new BehaviorSubject<NavigationMode>('assortment');
  public navigationMode$ = this._navigationMode.asObservable();
  public get navigationMode() { return this._navigationMode.value; }
  public set navigationMode(mode: NavigationMode) { this._navigationMode.next(mode); }

  private destroy$ = new Subject();

  constructor(
    private _http: HttpClient,
    private _loader: LoaderService,
    private _title: Title,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
  ) {
    this.determineShopMode();
    this.determineCurrentCart();
    this.determineCurrentShowcase();
  }

  reset() {
    // this.setCurrentChannel(null);
    // this.setCurrentPos(null);
    // this.setShopMode(null);
  }

  public updateUserCurrentTenant(loaderId: string, tenantId: string) {
    if (loaderId) this._loader.showLoader(loaderId);
    return this._http.post(this.api + '/users/switch-tenant/' + tenantId, null).pipe(
      tap(() => {
        if (loaderId) this._loader.dismissLoader(loaderId);
      })
    );;
  }

  public getContext(loaderId: string): Observable<{ UserInfo: UserInfo, Stores: Shop[] }> {
    if (loaderId) this._loader.showLoader(loaderId);
    return combineLatest([this.getUserInfo(), this.getShops()]).pipe(map(([UserInfo, Stores]) => {
      if (loaderId) this._loader.dismissLoader(loaderId);
      this._contextSub.next({ UserInfo, Stores });
      return { UserInfo, Stores };
    }));
  }

  getUserInfo(loaderId?: string): Observable<UserInfo> {
    if (!this.userInfo?.value) {
      if (loaderId) this._loader.showLoader(loaderId);
      return this._http.get<UserInfo>(`${this.api}/users/user-info`)
        .pipe(
          map(data => new UserInfo(data)),
          tap(userInfo => {
            if (loaderId) this._loader.dismissLoader(loaderId);
            this.userInfo.next(userInfo);
            if (userInfo.mainRole <= RolesEnum.Customer) this.checkNavigationMode();
            if (userInfo.mainRole === RolesEnum.CustomerGuest) this.setIsUserGuest(true);
          })
        );
    } else return of(this.userInfo.value);
  }

  public getChannels(): Observable<PagedResponse<ChannelDTO>> {
    const params = { maxResultCount: 0, skipCount: 0 };
    return this._http.get<PagedResponse<ChannelDTO>>(this.api + '/channels', { params });
  }

  getShops(params?: IPagedRequest): Observable<Shop[]> {
    const _sub = new Subject<Shop[]>();
    if (!this._contextSub.value?.Stores?.length) {
      this._http.get<PagedResponse<Shop>>(`${this.api}/stores`, {
        params: {
          skipCount: params?.skipCount?.toString() || "0",
          maxResultCount: params?.maxResultCount?.toString() || "0",
        }
      }).subscribe(data => {
        data.items = data.items.filter(s => s.uri?.toLowerCase().indexOf('https') >= 0);
        data.items.sort((a, b) => a.id - b.id);
        data.items = data.items.map(x => new Shop(x));
        _sub.next(data.items);
      });
    }
    else setTimeout(() => _sub.next(this._contextSub.value.Stores));
    return _sub.asObservable();
  }

  setChannels(channels: ActiveChannelDTO[]) {
    this._channels.next(channels);
  }

  setCurrentShop(shop: Shop) {
    localStorage.setItem('shopId', shop?.id?.toString());
    this._title.setTitle(shop.name);
    const favIcon: HTMLLinkElement = document.querySelector('#appIcon');
    if (favIcon) favIcon.href = shop?.faviconMediaUrl || 'favicon.ico';
    this.currentShop.next(shop);
  }

  setCurrentChannel(channel: ActiveChannelDTO) {
    localStorage.setItem('channelId', channel?.id?.toString());

    if (channel?.channel?.supportsStockLimits) {
      this.currentNavigationOnlyRetailMode = { value: true, blockUpdateLocalStorage: true };
    }

    this.currentActivatedChannel.next(channel);
    this.setCurrentChannelName(channel);
  }

  setCurrentPos(pos: PointOfSaleDto) {
    this.currentPos.next(pos);
    if (pos) {
      this._channels.next(pos.activatedStoreChannels.filter(c => c.storeId === this.currentShopValue.id));
      if (this.currentShopValue) {
        const context: StorageContextModel = {
          posId: pos.id,
          userId: this.userInfoChangedValue?.id
        };
        localStorage.setItem('4shop-context-store-' + this.currentShopValue.id, JSON.stringify(context));
      }
    }
  }

  setIsUserGuest(value: boolean) {
    this.currentNavigationOnlyRetailMode = { value: value, blockUpdateLocalStorage: true };
    this.isUserGuest.next(value);
  }


  getPointsOfSale(params?: IPagedRequest, externalId?: string) {
    return this._http.get<PagedResponse<PointOfSaleDto>>(`${this.api}/point-of-sales`, {
      params: {
        skipCount: params?.skipCount?.toString(),
        maxResultCount: params?.maxResultCount?.toString(),
        sorting: params?.sorting,
        externalId: externalId
      }
    }).pipe(
      map(data => {
        data.items = data.items.map(x => new PointOfSaleDto(x));
        return data;
      })
    );
  }

  public setShopLoading(value: boolean) {
    if (this.shopLoading.value === value) {
      return;
    }
    this.shopLoading.next(value);
  }

  public prepareParams(params: any) {
    const output: any = {};
    if (!params) {
      return output;
    }
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null &&
        (typeof params[key] === 'string' || !isNaN(params[key]) || params[key]?.length > 0)) {
        output[key] = params[key];
      }
    });
    return output;
  }

  private setCurrentChannelName(channel: ActiveChannelDTO) {
    if (channel && channel.channel.supportsPreOrders) {
      this.currentChannelName.next(channel.channel.name);
    }
  }

  public setLanguage(lang: UserCultureEnum) {
    const infoUser = this.userInfo.value;
    infoUser.culture = lang;
    this.userInfo.next(infoUser);
  }

  private determineShopMode() {
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._activatedRoute),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      switchMap((route) => route.data)
    ).subscribe((event) => {
      if (event['route'] === OrderTypeEnum.PreOrder && this.currentShopMode.value !== OrderTypeEnum.PreOrder) this.setShopMode(OrderTypeEnum.PreOrder);
      if (event['route'] === OrderTypeEnum.ReOrder && this.currentShopMode.value !== OrderTypeEnum.ReOrder) this.setShopMode(OrderTypeEnum.ReOrder);
    });
  }

  private determineCurrentShowcase() {
    const activeShowcase = localStorage.getItem('activeShowcase');
    if (activeShowcase) {
      this.currentShowcase = JSON.parse(activeShowcase);
    }
  }

  public setShopMode(mode: OrderTypeEnum) {
    this.currentShopMode.next(mode);
    localStorage.setItem('navigationMode', mode === OrderTypeEnum.ReOrder ? 're-order' : 'pre-order');
  }

  updateLocalStorageIfChanged(key: string, newObject: any): boolean {
    // Get existing data from localStorage
    const storedData = localStorage.getItem(key);

    // Convert new object to JSON string for comparison
    const newObjectString = JSON.stringify(newObject);

    // If nothing stored yet, or stored data is different, update localStorage
    if (storedData === null || storedData !== newObjectString) {
      localStorage.setItem(key, newObjectString);
      return true; // Updated
    }

    return false; // No update needed
  }

  private determineCurrentCart() {
    combineLatest([
      this.selectedPreorderCartChanged,
      this.selectedReorderCartChanged
    ]).pipe(
      debounceTime(100),
      filter(() => this.currentShopModeValue != undefined && this.currentShopModeValue != null),
      map(([preOrderCart, reOrderCart]) => this.currentShopModeValue === OrderTypeEnum.PreOrder ? preOrderCart : reOrderCart)
    ).subscribe((cart) => {
      this.currentSelectedCart.next(cart);
    });
  }

  private checkNavigationMode() {
    timer(0, 500).pipe(
      switchMap(() => of(localStorage.getItem('onlyRetailMode'))),
      distinctUntilChanged(),
      filter(value => !!value),
      map(value => value === 'true'),
      takeUntil(this.destroy$),
    ).subscribe((value) => {
      this.currentNavigationOnlyRetailMode = { value };
    });
  }

  private emitDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private detectCurrentOS() {
    const userAgent = window.navigator.userAgent,
      platform = (window as any).navigator?.userAgentData?.platform || window.navigator.platform,
      macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
      windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
      iosPlatforms = ['iPhone', 'iPad', 'iPod'];
    let os: CurrentOS = CurrentOS.Unknown;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.MacOS;
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.iOS;
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = CurrentOS.Windows;
    } else if (/Android/.test(userAgent)) {
      os = CurrentOS.Android;
    } else if (/Linux/.test(platform)) {
      os = CurrentOS.Linux;
    }

    return { os, isMobile };
  }
}
